/**
 * C4 · Guided exercise player: reference pattern, count-in, scrolling target.
 * C5 · Live pitch trace + accuracy score (pitch & timing only — J6).
 * C6 · Tempo/key adjustments within coach-set limits.
 * D1/D2 · Record → private listen-back → submit (or discard).
 * J2 latency offset, J3 noise-floor warning, C8 offline fallback.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  FEEDBACK_COPY,
  midiToName,
  renderCallResponse,
  renderPattern,
  scoreTrace,
  segmentAt,
  type ExercisePattern,
  type TracePoint,
} from '@solfo/shared';
import { api, fetchFileUrl, uploadFile } from '../../lib/api.ts';
import { t, exName as exLabel, exDesc as exDescription } from '../../lib/i18n.ts';
import { Button, Card, ErrorText, Spinner } from '../../lib/ui.tsx';
import PitchCanvas from '../../components/PitchCanvas.tsx';
import { MicSession, NOISE_FLOOR_WARN_RMS } from '../../audio/mic.ts';
import { audioContext, playExercise, type PlaybackHandle } from '../../audio/synth.ts';

interface Exercise {
  id: string;
  name: string;
  kind: string;
  mode?: string; // "standard" | "call_response" (ear training)
  description: string;
  patternJson: string | null;
  audioFileId: string | null;
  videoUrl: string | null;
  defaultTempoBpm: number;
  minTempoBpm: number | null;
  maxTempoBpm: number | null;
  transposeSemisLimit: number;
}

type Phase = 'loading' | 'ready' | 'noise-check' | 'running' | 'done' | 'error';

export default function Player() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const exerciseId = params.get('exerciseId')!;
  const planItemId = params.get('planItemId');
  const mode = (params.get('mode') ?? 'plan') as 'plan' | 'warmup';
  const baseRoot = params.get('rootMidi') ? Number(params.get('rootMidi')) : null;
  const baseTempo = params.get('tempoBpm') ? Number(params.get('tempoBpm')) : null;

  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<unknown>(null);
  const [semisShift, setSemisShift] = useState(0); // C6 key shift
  const [tempoShift, setTempoShift] = useState(0); // C6 tempo delta
  const [noiseWarning, setNoiseWarning] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [trace, setTrace] = useState<TracePoint[]>([]);
  const [result, setResult] = useState<{ score: number | null; meanAbsCents: number | null } | null>(null);
  const [takeBlob, setTakeBlob] = useState<Blob | null>(null);
  const [takeUrl, setTakeUrl] = useState<string | null>(null);
  const [submitState, setSubmitState] = useState<'idle' | 'saving' | 'submitted' | 'discarded'>('idle');
  const [customAudioUrl, setCustomAudioUrl] = useState<string | null>(null);

  const micRef = useRef<MicSession | null>(null);
  const handleRef = useRef<PlaybackHandle | null>(null);
  const rafRef = useRef(0);
  const traceRef = useRef<TracePoint[]>([]);
  const startedAtRef = useRef(0);
  /** Seconds into the recorded file at which exercise beat 1 occurs (sync for D4 overlay). */
  const syncOffsetRef = useRef(0);
  /** Actual sung duration (finish-early aware). */
  const durationRef = useRef(0);

  // ── load exercise (network → offline bundle fallback, C8) ────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const ex = await api<Exercise>(`/api/exercises/${exerciseId}`);
        if (!cancelled) {
          setExercise(ex);
          setPhase('ready');
        }
      } catch {
        const raw = localStorage.getItem('solfo.offlineBundle');
        const bundle = raw ? (JSON.parse(raw) as { exercises: Exercise[] }) : null;
        const ex = bundle?.exercises.find((e) => e.id === exerciseId);
        if (!cancelled) {
          if (ex) {
            setExercise(ex);
            setPhase('ready');
          } else {
            setError(new Error('Exercise unavailable offline'));
            setPhase('error');
          }
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [exerciseId]);

  useEffect(() => {
    if (exercise?.kind === 'custom_audio' && exercise.audioFileId) {
      void fetchFileUrl(exercise.audioFileId).then(setCustomAudioUrl).catch(() => {});
    }
  }, [exercise]);

  const pattern = useMemo<ExercisePattern | null>(
    () => (exercise?.patternJson ? (JSON.parse(exercise.patternJson) as ExercisePattern) : null),
    [exercise],
  );
  const rootMidi = (baseRoot ?? 60) + semisShift;
  const tempoBpm = clampTempo((baseTempo ?? pattern?.defaultTempoBpm ?? exercise?.defaultTempoBpm ?? 100) + tempoShift, exercise);
  // Ear training (call & response): each phrase plays first, the student
  // echoes it into an equal-length response window; targets stay hidden
  // until the run is over.
  const isEcho = exercise?.mode === 'call_response' && !!pattern;
  const echoRender = useMemo(
    () => (isEcho && pattern ? renderCallResponse(pattern, rootMidi, tempoBpm) : null),
    [isEcho, pattern, rootMidi, tempoBpm],
  );
  const rendered = useMemo(
    () =>
      echoRender ? echoRender.playback : pattern ? renderPattern(pattern, rootMidi, tempoBpm) : null,
    [echoRender, pattern, rootMidi, tempoBpm],
  );
  /** Timeline the trace is scored against and revealed over (D4). */
  const scoringTarget = echoRender ? echoRender.target : rendered;
  const hasPitchTarget = !!scoringTarget && scoringTarget.notes.some((n) => n.midi !== null);
  const currentSegment = echoRender ? segmentAt(echoRender.segments, currentTime) : null;

  // ── run ───────────────────────────────────────────────────────────────────
  async function start() {
    setError(null);
    setTrace([]);
    traceRef.current = [];
    setResult(null);
    setTakeBlob(null);
    setTakeUrl(null);
    setSubmitState('idle');
    try {
      const mic = new MicSession();
      await mic.open();
      micRef.current = mic;

      setPhase('noise-check');
      const noise = await mic.measureNoiseFloor(); // J3
      setNoiseWarning(noise > NOISE_FLOOR_WARN_RMS);

      const latencyMs = Number(localStorage.getItem('solfo.latencyMs') ?? 0); // J2
      const ac = audioContext();

      let handle: PlaybackHandle | null = null;
      let audioEl: HTMLAudioElement | null = null;
      let totalSec: number;
      if (rendered) {
        handle = playExercise(rendered, { tempoBpm, countInBeats: 4 });
        handleRef.current = handle;
        totalSec = rendered.totalSec;
      } else if (customAudioUrl) {
        audioEl = new Audio(customAudioUrl);
        await audioEl.play();
        totalSec = Number.isFinite(audioEl.duration) ? audioEl.duration : 120;
        handleRef.current = {
          startTime: ac.currentTime,
          countInSec: 0,
          stop: () => audioEl?.pause(),
        };
        handle = handleRef.current;
      } else {
        throw new Error('Nothing to play');
      }

      const clock = () => ac.currentTime - handle!.startTime;
      mic.startRecording(); // D1
      startedAtRef.current = performance.now();
      mic.startTracking(
        clock,
        (p) => {
          traceRef.current.push(p);
        },
        latencyMs,
      );
      setPhase('running');

      const tick = () => {
        const tNow = clock();
        setCurrentTime(tNow);
        setTrace([...traceRef.current]);
        if (tNow >= totalSec + 0.4) {
          void finish(totalSec);
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      setError(err);
      setPhase('error');
      cleanup();
    }
  }

  async function finish(totalSec: number) {
    cancelAnimationFrame(rafRef.current);
    const handle = handleRef.current;
    handle?.stop();
    const mic = micRef.current;
    // The recording began before the count-in — remember where beat 1 sits
    // inside the file so playback overlays stay in sync (D4). The singer's
    // voice lands in the file a round-trip (speaker→ear→mic) later than the
    // scheduled clock, so fold that in too: the J2-calibrated value when
    // available, otherwise the AudioContext's own latency estimate.
    if (handle) {
      const ac = audioContext();
      const recStart = mic?.recordingStartedAt ?? handle.startTime - handle.countInSec - 0.15;
      const roundTripSec =
        Number(localStorage.getItem('solfo.latencyMs') ?? 0) / 1000 ||
        (ac.outputLatency || 0) + (ac.baseLatency || 0);
      syncOffsetRef.current = Math.max(0, handle.startTime - recStart + roundTripSec);
      durationRef.current = totalSec;
    }
    let blob: Blob | null = null;
    if (mic) {
      mic.stopTracking();
      try {
        blob = await mic.stopRecording();
      } catch {
        /* recording unsupported */
      }
      mic.close();
    }
    setTakeBlob(blob);
    if (blob) setTakeUrl(URL.createObjectURL(blob));

    let score: number | null = null;
    let meanAbsCents: number | null = null;
    if (scoringTarget && hasPitchTarget) {
      const r = scoreTrace(traceRef.current, scoringTarget);
      score = r.score;
      meanAbsCents = r.meanAbsCents;
    }
    setResult({ score, meanAbsCents });
    setPhase('done');

    // C5/C8 · log the practice result (queue offline)
    const payload = {
      planItemId: planItemId ?? null,
      exerciseId,
      mode,
      accuracyScore: score,
      durationSec: Math.round(totalSec),
      rootMidi,
      tempoBpm,
      clientCreatedAt: new Date().toISOString(),
    };
    try {
      await api('/api/practice-results', { body: payload });
    } catch {
      const queue = JSON.parse(localStorage.getItem('solfo.resultQueue') ?? '[]') as unknown[];
      queue.push(payload);
      localStorage.setItem('solfo.resultQueue', JSON.stringify(queue));
    }
  }

  function stopEarly() {
    const ac = audioContext();
    const elapsed = handleRef.current ? ac.currentTime - handleRef.current.startTime : 0;
    void finish(Math.max(0, elapsed));
  }

  function cleanup() {
    cancelAnimationFrame(rafRef.current);
    handleRef.current?.stop();
    micRef.current?.close();
    micRef.current = null;
  }
  useEffect(() => cleanup, []);

  // D2 · submit the take
  async function submitTake() {
    if (!takeBlob) return;
    setSubmitState('saving');
    try {
      const file = await uploadFile(takeBlob, `take-${Date.now()}.webm`);
      const take = await api<{ id: string }>('/api/takes', {
        body: {
          exerciseId,
          planItemId: planItemId ?? null,
          fileId: file.id,
          pitchTraceJson: JSON.stringify(traceRef.current.filter((_, i) => i % 2 === 0)),
          targetJson: scoringTarget ? JSON.stringify(scoringTarget) : null,
          accuracyScore: result?.score ?? null,
          durationSec: Math.round(durationRef.current || rendered?.totalSec || 0),
          syncOffsetSec: syncOffsetRef.current,
        },
      });
      await api(`/api/takes/${take.id}/submit`, { body: {} });
      setSubmitState('submitted');
    } catch (err) {
      setError(err);
      setSubmitState('idle');
    }
  }

  if (phase === 'loading') return <Spinner />;
  if (!exercise) return <ErrorText error={error ?? 'Not found'} />;

  return (
    <div className="mx-auto flex min-h-dvh max-w-lg flex-col">
      <header className="flex items-center justify-between px-4 py-3">
        <button onClick={() => { cleanup(); navigate(-1); }} className="text-sm text-slate-500">← Back</button>
        <h1 className="truncate text-sm font-semibold">{exLabel(exercise)}</h1>
        <span className="w-12 text-right text-xs text-slate-400">{mode === 'warmup' ? '🔥' : ''}</span>
      </header>

      <main className="flex-1 space-y-4 px-4 pb-8">
        {rendered ? (
          <PitchCanvas
            rendered={scoringTarget ?? rendered}
            trace={trace}
            currentTime={phase === 'running' ? currentTime : phase === 'done' ? rendered.totalSec : 0}
            mode={phase === 'done' ? 'full' : 'scroll'}
            height={260}
            // ear training: the target stays hidden until the run is over
            showTargets={!isEcho || phase === 'done'}
          />
        ) : (
          <Card>
            <p className="text-sm text-slate-600">{exDescription(exercise)}</p>
            {customAudioUrl && phase !== 'running' && <audio controls src={customAudioUrl} className="mt-3 w-full" />}
          </Card>
        )}

        {noiseWarning && phase !== 'done' && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">
            {t('player.noise', FEEDBACK_COPY.noiseWarning)}
          </p>
        )}

        {(phase === 'ready' || phase === 'error') && (
          <>
            {exDescription(exercise) && rendered && (
              <p className="text-sm text-slate-600">{exDescription(exercise)}</p>
            )}
            {isEcho && (
              <p className="rounded-lg bg-brand-50 px-3 py-2 text-xs text-brand-800">
                👂{' '}
                {t(
                  'player.echoHint',
                  'Ear training: each phrase plays first, then you sing it back from memory. The targets are revealed at the end.',
                )}
              </p>
            )}
            {exercise.videoUrl && (
              <a href={exercise.videoUrl} target="_blank" rel="noreferrer" className="block text-sm font-medium text-brand-700">
                🎬 Watch the demo video
              </a>
            )}
            {/* C6 · adjustments within coach limits */}
            {rendered && (
              <Card title="Adjust (within your coach's limits)">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="mb-1 text-xs text-slate-500">Key · starts at {midiToName(rootMidi)}</p>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" onClick={() => setSemisShift((s) => Math.max(s - 1, -exercise.transposeSemisLimit))} disabled={semisShift <= -exercise.transposeSemisLimit}>−1</Button>
                      <span className="min-w-8 text-center text-sm font-medium">{semisShift >= 0 ? `+${semisShift}` : semisShift}</span>
                      <Button variant="secondary" onClick={() => setSemisShift((s) => Math.min(s + 1, exercise.transposeSemisLimit))} disabled={semisShift >= exercise.transposeSemisLimit}>+1</Button>
                    </div>
                  </div>
                  <div>
                    <p className="mb-1 text-xs text-slate-500">Tempo · {tempoBpm} BPM</p>
                    <div className="flex items-center gap-2">
                      <Button variant="secondary" onClick={() => setTempoShift((s) => s - 5)} disabled={tempoBpm <= (exercise.minTempoBpm ?? Math.round((baseTempo ?? 100) * 0.7))}>−5</Button>
                      <Button variant="secondary" onClick={() => setTempoShift((s) => s + 5)} disabled={tempoBpm >= (exercise.maxTempoBpm ?? Math.round((baseTempo ?? 100) * 1.15))}>+5</Button>
                    </div>
                  </div>
                </div>
              </Card>
            )}
            <Button onClick={start} className="w-full py-3 text-base">
              ▶ Start (4-beat count-in)
            </Button>
            <ErrorText error={error} />
          </>
        )}

        {phase === 'noise-check' && <Spinner label="Checking your room noise…" />}

        {phase === 'running' && (
          <div className="space-y-3 text-center">
            {isEcho ? (
              currentTime < 0 ? (
                <p className="text-sm text-slate-500">{t('player.countIn', 'Count-in…')}</p>
              ) : currentSegment?.kind === 'response' ? (
                <p className="rounded-xl bg-brand-50 px-3 py-2 text-sm font-semibold text-brand-800">
                  🎤 {t('player.echoSing', 'Your turn — sing it back!')}
                </p>
              ) : (
                <p className="rounded-xl bg-slate-100 px-3 py-2 text-sm font-semibold text-slate-700">
                  👂 {t('player.echoListen', 'Listen…')}
                </p>
              )
            ) : (
              <p className="text-sm text-slate-500">
                {currentTime < 0 ? t('player.countIn', 'Count-in…') : 'Sing along with the moving target 🎯'}
              </p>
            )}
            <Button variant="secondary" onClick={stopEarly}>Finish early</Button>
          </div>
        )}

        {phase === 'done' && (
          <div className="space-y-4">
            {result?.score !== null && result && (
              <Card className="animate-pop-in text-center">
                <p className="text-gradient-brand text-5xl font-extrabold tabular-nums">{result.score}</p>
                <p className="text-xs text-slate-500">{t('player.score', FEEDBACK_COPY.scoreExplanation)}</p>
                {result.meanAbsCents !== null && result.meanAbsCents > 0 && (
                  <p className="mt-1 text-xs text-slate-400">avg deviation {Math.round(result.meanAbsCents)} cents</p>
                )}
                {/* J6 · anything beyond pitch & timing routes to the coach */}
                <p className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-500">
                  {t('player.askCoach', FEEDBACK_COPY.askYourCoach)}
                </p>
              </Card>
            )}

            {/* D1 · private listen-back before deciding */}
            {takeUrl && submitState !== 'submitted' && submitState !== 'discarded' && (
              <Card title={t('player.listen', 'Listen back (only you can hear this)')}>
                <audio controls src={takeUrl} className="w-full" />
                <div className="mt-3 flex gap-2">
                  <Button onClick={submitTake} disabled={submitState === 'saving'} className="flex-1">
                    {submitState === 'saving' ? 'Sending…' : t('player.submit', 'Submit to coach')}
                  </Button>
                  <Button variant="secondary" onClick={() => setSubmitState('discarded')}>
                    {t('player.discard', 'Discard')}
                  </Button>
                </div>
              </Card>
            )}
            {submitState === 'submitted' && (
              <p className="rounded-lg bg-emerald-50 px-3 py-2 text-center text-sm font-medium text-emerald-800">
                Sent to your coach ✅ — you'll get a note when it's reviewed.
              </p>
            )}
            {submitState === 'discarded' && (
              <p className="text-center text-xs text-slate-400">Take discarded — it never left your device's draft space.</p>
            )}

            <div className="flex gap-2">
              <Button variant="secondary" onClick={start} className="flex-1">↻ Again</Button>
              <Link to={mode === 'warmup' ? '/app/warmup' : '/app'} className="flex-1">
                <Button variant="secondary" className="w-full">Done</Button>
              </Link>
            </div>
            <ErrorText error={error} />
          </div>
        )}
      </main>
    </div>
  );
}

function clampTempo(bpm: number, exercise: Exercise | null): number {
  if (!exercise) return bpm;
  const min = exercise.minTempoBpm ?? 40;
  const max = exercise.maxTempoBpm ?? 220;
  return Math.max(min, Math.min(max, bpm));
}
